import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';

import { Divider, Grid, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// third-party

// project imports

// ==============================|| CALENDAR EVENT ADD / EDIT / DELETE ||============================== //

// Attendance values in column order: Present, Absent, N/A
const ATTENDANCE_COLUMNS = [
    { value: 2, label: 'Present' },
    { value: 1, label: 'Absent' },
    { value: 3, label: 'N/A' }
];

const TakeAttendanceForm = ({
    event,
    onSubmit,
    isFutureSession,
    isConfirmedSession,
    disableFields,
    isSavingAttendance,
    setIsSavingAttendance
}) => {
    const [students, setStudents] = useState([]);
    const [attendanceChanged, setAttendanceChanged] = useState(false);
    const [markAllPresent, setMarkAllPresent] = useState(false);
    const [disabledSession, setDisabledSession] = useState(false);

    const check = (id, value) => {
        console.log(value);
        const studentsCopy = students.map((s) => (s.studentId === id ? { ...s, attendance: value } : s));
        setStudents(studentsCopy);
        setAttendanceChanged(true);
    };

    // Runs again when the session is reloaded (e.g. after a refused submit). Marks already ticked here are kept,
    // except for a student who is blocked now, who goes back to their saved mark.
    useEffect(() => {
        if (event.students) {
            setStudents((prev) =>
                event.students.map((s) => {
                    const saved = s.attendance ? s.attendance.attendance : 0;
                    const current = prev.find((p) => p.classParticipantId === s.classParticipantId);
                    return {
                        studentId: s.studentId,
                        studentName: s.studentName,
                        studentFirstName: s.studentFirstName,
                        studentMiddleName: s.studentMiddleName ? s.studentMiddleName : '',
                        studentLastName: s.studentLastName,
                        classParticipantId: s.classParticipantId,
                        // A blocked student always shows their saved mark, so for them attendance is the saved mark
                        attendance: current && !s.assessmentBlocked ? current.attendance : saved,
                        attendanceId: s.attendance ? s.attendance.attendanceId : '',
                        assessmentBlocked: s.assessmentBlocked
                    };
                })
            );
        }
        if (isFutureSession || isConfirmedSession || disableFields) {
            setDisabledSession(true);
        }
    }, [event.students]);

    // Students waiting on an assessment score can't be marked
    const allBlocked = students.length > 0 && students.every((s) => s.assessmentBlocked);

    const handleMarkAll = (value) => {
        setMarkAllPresent(value);
        const studentsCopy = students.map((s) => (s.assessmentBlocked ? s : { ...s, attendance: value ? 2 : 0 }));
        setStudents(studentsCopy);
        setAttendanceChanged(true);
    };

    const handleSave = async () => {
        setIsSavingAttendance(true);
        await onSubmit(students);
        setIsSavingAttendance(false);
    };

    return students.length === 0 ? (
        <p>There are no students in this class</p>
    ) : (
        <div>
            <Grid>
                <FormControlLabel
                    sx={{ ml: 2, float: 'right' }}
                    label="Mark all present"
                    control={<Checkbox checked={markAllPresent} onChange={(e) => handleMarkAll(e.target.checked)} />}
                    disabled={disabledSession || allBlocked}
                />
                <TableContainer component={Paper}>
                    <Table aria-label="simple table" size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Student</TableCell>
                                <TableCell align="center">Present</TableCell>
                                <TableCell align="center">Absent</TableCell>
                                <Tooltip placement="bottom" title="Student in school but unavailable to attend session">
                                    <TableCell align="center">N/A</TableCell>
                                </Tooltip>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {students.map((student) => {
                                const blockedNote = student.assessmentBlocked && !student.attendance;
                                return (
                                    // The reason is on the whole row. A tap shows it on a phone, and the row takes focus so a
                                    // keyboard can reach it, as its checkboxes are disabled.
                                    <Tooltip
                                        key={student.classParticipantId}
                                        title={blockedNote ? `No Term ${event.assessmentTerm} score` : ''}
                                        placement="bottom-start"
                                        enterDelay={0}
                                        enterTouchDelay={0}
                                        describeChild
                                    >
                                        <TableRow
                                            tabIndex={blockedNote ? 0 : undefined}
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                // A grey row, with text that stays readable (grey 700 on grey 100 is about 6:1)
                                                ...(student.assessmentBlocked && {
                                                    backgroundColor: 'grey.100',
                                                    '& th': { color: 'grey.700' }
                                                })
                                            }}
                                        >
                                            <TableCell key={0} align="left" component="th" scope="row">
                                                {student.studentLastName}, {student.studentFirstName} {student.studentMiddleName}
                                            </TableCell>
                                            {ATTENDANCE_COLUMNS.map((column) => (
                                                <TableCell key={column.value} align="center">
                                                    <Checkbox
                                                        checked={student.attendance === column.value}
                                                        onChange={() => check(student.studentId, column.value)}
                                                        disabled={disabledSession || student.assessmentBlocked}
                                                        inputProps={{
                                                            'aria-label': `${student.studentLastName}, ${student.studentFirstName}: ${column.label}`
                                                        }}
                                                    />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </Tooltip>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            <Divider style={{ marginTop: '30px', width: '100%' }} />
            <Box textAlign="center" sx={{ mt: 2 }}>
                <LoadingButton
                    loading={isSavingAttendance}
                    loadingPosition="start"
                    variant="contained"
                    startIcon={<DoneAllIcon />}
                    onClick={handleSave}
                    disabled={(event.attendanceTaken && !attendanceChanged) || disabledSession || allBlocked}
                >
                    Submit
                </LoadingButton>
            </Box>
        </div>
    );
};

TakeAttendanceForm.propTypes = {
    // The session, with students[].assessmentBlocked and assessmentTerm from GET Session/{id}
    event: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isFutureSession: PropTypes.bool,
    isConfirmedSession: PropTypes.bool,
    disableFields: PropTypes.bool,
    isSavingAttendance: PropTypes.bool,
    setIsSavingAttendance: PropTypes.func.isRequired
};

export default TakeAttendanceForm;
